// Cartomancer API client - uses Mytharion backend
const BASE = process.env.REACT_APP_BACKEND_URL;

async function request(path, options = {}) {
  const res = await fetch(`${BASE}/api/cartomancer${path}`, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
    ...options,
  });
  if (!res.ok) {
    let msg;
    try {
      const data = await res.json();
      msg = data.detail || data.message || `HTTP ${res.status}`;
    } catch {
      msg = `HTTP ${res.status}`;
    }
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  if (res.status === 204) return null;
  const ct = res.headers.get("content-type") || "";
  return ct.includes("application/json") ? res.json() : res.text();
}

export const cartomancerApi = {
  getAccess: () => request("/access"),
  listProjects: () => request("/projects"),
  getProject: (id) => request(`/projects/${id}`),
  createProject: (name) =>
    request("/projects", { method: "POST", body: JSON.stringify({ name }) }),
  renameProject: (id, name) =>
    request(`/projects/${id}`, { method: "PATCH", body: JSON.stringify({ name }) }),
  deleteProject: (id) => request(`/projects/${id}`, { method: "DELETE" }),
  getSnapshot: (id) => request(`/projects/${id}/snapshot`),
  saveSnapshot: (id, body) =>
    request(`/projects/${id}/snapshot`, {
      method: "PUT",
      body: JSON.stringify(body || {}),
    }),

  uploadMap: async (projectId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(
      `${BASE}/api/cartomancer/maps/upload?project_id=${encodeURIComponent(projectId)}`,
      { method: "POST", body: fd, credentials: "include" }
    );
    if (!res.ok) {
      let msg;
      try {
        msg = (await res.json()).detail;
      } catch {
        msg = `HTTP ${res.status}`;
      }
      const err = new Error(msg);
      err.status = res.status;
      throw err;
    }
    return res.json();
  },
  listMaps: (projectId) => request(`/maps/list?project_id=${encodeURIComponent(projectId)}`),
  updateScale: (mapId, payload) =>
    request(`/maps/${mapId}/scale`, { method: "PATCH", body: JSON.stringify(payload) }),
  updateDate: (mapId, payload) =>
    request(`/maps/${mapId}/date`, { method: "PATCH", body: JSON.stringify(payload) }),
  advanceMap: (mapId, days) =>
    request(`/maps/${mapId}/advance`, { method: "POST", body: JSON.stringify({ days }) }),
  deleteMap: (mapId) => request(`/maps/${mapId}`, { method: "DELETE" }),

  listLocations: (mapId) => request(`/locations?map_id=${encodeURIComponent(mapId)}`),
  createLocation: (payload) =>
    request(`/locations`, { method: "POST", body: JSON.stringify(payload) }),
  updateLocation: (id, payload) =>
    request(`/locations/${id}`, { method: "PUT", body: JSON.stringify(payload) }),
  deleteLocation: (id) => request(`/locations/${id}`, { method: "DELETE" }),

  listPaths: (mapId) => request(`/paths?map_id=${encodeURIComponent(mapId)}`),
  createPath: (payload) =>
    request(`/paths`, { method: "POST", body: JSON.stringify(payload) }),
  deletePath: (id) => request(`/paths/${id}`, { method: "DELETE" }),

  // Settings
  getSettings: () => request("/settings"),
  updateSettings: (payload) =>
    request("/settings", { method: "PUT", body: JSON.stringify(payload) }),

  // Logs
  listLogs: (projectId) => request(`/logs?project_id=${encodeURIComponent(projectId)}`),
  createLog: (payload) =>
    request("/logs", { method: "POST", body: JSON.stringify(payload) }),
  deleteLog: (id) => request(`/logs/${id}`, { method: "DELETE" }),
  generateJournalEntry: (id) =>
    request(`/logs/${id}/journal`, { method: "POST" }),

  // Calendar
  listCalendarPresets: () => request("/calendar/presets"),
};

export function mapFileUrl(storagePath) {
  return `${BASE}/api/cartomancer/files/${storagePath}`;
}

// ---------- FANTASY PIN CATEGORIES ----------
// Icons from lucide-react
export const LOCATION_CATEGORIES = [
  { id: "city",       label: "Città",        color: "#d4af37", icon: "Landmark" },
  { id: "village",    label: "Villaggio",    color: "#f59e0b", icon: "Home" },
  { id: "castle",     label: "Castello",     color: "#a78bfa", icon: "Castle" },
  { id: "dungeon",    label: "Dungeon",      color: "#9f1239", icon: "Skull" },
  { id: "temple",     label: "Tempio",       color: "#fbbf24", icon: "Church" },
  { id: "ruin",       label: "Rovine",       color: "#94a3b8", icon: "Building2" },
  { id: "port",       label: "Porto",        color: "#38bdf8", icon: "Ship" },
  { id: "camp",       label: "Base",         color: "#84cc16", icon: "Tent" },
  { id: "fortress",   label: "Fortezza",     color: "#e11d48", icon: "Shield" },
  { id: "tower",      label: "Torre",        color: "#c084fc", icon: "Building" },
  { id: "market",     label: "Mercato",      color: "#fb923c", icon: "Store" },
  { id: "lair",       label: "Covo",         color: "#6b7280", icon: "Bone" },
  { id: "danger",     label: "Pericolo",     color: "#dc2626", icon: "TriangleAlert" },
];

export const TERRAIN_TYPES = [
  { id: "plain",    label: "Pianura",   speedMod: 1.0 },
  { id: "road",     label: "Strada",    speedMod: 1.5 },
  { id: "forest",   label: "Foresta",   speedMod: 0.7 },
  { id: "hill",     label: "Collina",   speedMod: 0.75 },
  { id: "mountain", label: "Montagna",  speedMod: 0.5 },
  { id: "swamp",    label: "Palude",    speedMod: 0.4 },
  { id: "desert",   label: "Deserto",   speedMod: 0.6 },
  { id: "water",    label: "Mare",      speedMod: 1.0, marine: true },
  { id: "tundra",   label: "Tundra",    speedMod: 0.6 },
];

export const WEATHER_TYPES = [
  { id: "clear",  label: "Sereno",   speedMod: 1.0 },
  { id: "rain",   label: "Pioggia",  speedMod: 0.8 },
  { id: "storm",  label: "Tempesta", speedMod: 0.5 },
  { id: "snow",   label: "Neve",     speedMod: 0.5 },
  { id: "fog",    label: "Nebbia",   speedMod: 0.75 },
  { id: "wind",   label: "Vento",    speedMod: 0.9 },
  { id: "heat",   label: "Caldo Torrido", speedMod: 0.85 },
];

// Marine conditions used when a leg's terrain is "water" (Mare)
export const MARINE_CONDITIONS = [
  { id: "calm",             label: "Mare Calmo",     speedMod: 1.0 },
  { id: "moderate_waves",   label: "Onde Moderate",  speedMod: 0.85 },
  { id: "rough_sea",        label: "Mare Agitato",   speedMod: 0.65 },
  { id: "sea_storm",        label: "Tempesta",       speedMod: 0.45 },
  { id: "hurricane",        label: "Uragano",        speedMod: 0.2 },
];

// Helpers to resolve modifiers considering user overrides stored in settings.custom_modifiers.
// customMods shape: { terrain: {id: mod}, weather: {id: mod}, marine: {id: mod} }
export function getTerrainMod(id, customMods) {
  const t = TERRAIN_TYPES.find((x) => x.id === id) || TERRAIN_TYPES[0];
  const override = customMods?.terrain?.[t.id];
  return typeof override === "number" ? override : t.speedMod;
}

export function getWeatherMod(id, customMods) {
  const w = WEATHER_TYPES.find((x) => x.id === id) || WEATHER_TYPES[0];
  const override = customMods?.weather?.[w.id];
  return typeof override === "number" ? override : w.speedMod;
}

export function getMarineMod(id, customMods) {
  const m = MARINE_CONDITIONS.find((x) => x.id === id) || MARINE_CONDITIONS[0];
  const override = customMods?.marine?.[m.id];
  return typeof override === "number" ? override : m.speedMod;
}

export function isMarineTerrain(terrainId) {
  const t = TERRAIN_TYPES.find((x) => x.id === terrainId);
  return !!t?.marine;
}

export const ENVIRONMENTS = [
  { id: "wilderness", label: "Terre Selvagge" },
  { id: "road",       label: "Strada Maestra" },
  { id: "urban",      label: "Urbano" },
  { id: "underground", label: "Sotterraneo" },
  { id: "coast",      label: "Costa" },
  { id: "sea",        label: "Mare Aperto" },
];

export const CLOCK_STYLES = [
  { id: "astrolabio", label: "Astrolabio" },
  { id: "meridiana",  label: "Meridiana" },
  { id: "cronografo", label: "Cronografo" },
];

export const EVENT_TYPES = [
  { id: "peaceful",   label: "Nessun Evento", hours: 0 },
  { id: "short_rest", label: "Riposo Breve",         hours: 1 },
  { id: "long_rest",  label: "Riposo Lungo",         hours: 8 },
  { id: "encounter",  label: "Evento",             hours: 0 },
];